import React, { useState } from 'react';
import { format, startOfMonth, endOfMonth, eachDayOfInterval, isSameMonth, isSameDay, addMonths, subMonths } from 'date-fns';
import { motion } from 'framer-motion';
import SafeIcon from '../../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const { FiChevronLeft, FiChevronRight, FiCalendar, FiClock, FiBriefcase } = FiIcons;

const DashboardCalendar = ({ tasks, projects }) => {
  const [currentMonth, setCurrentMonth] = useState(new Date());

  const days = eachDayOfInterval({
    start: startOfMonth(currentMonth),
    end: endOfMonth(currentMonth),
  });

  const nextMonth = () => setCurrentMonth(addMonths(currentMonth, 1));
  const prevMonth = () => setCurrentMonth(subMonths(currentMonth, 1));

  const getEventsForDay = (day) => {
    const dayTasks = tasks.filter(t => t.dueDate && isSameDay(new Date(t.dueDate), day));
    const dayProjects = projects.filter(p => p.deadline && isSameDay(new Date(p.deadline), day));
    return [...dayTasks.map(t => ({ ...t, type: 'task' })), ...dayProjects.map(p => ({ ...p, type: 'project' }))];
  };

  return (
    <div className="bg-white p-8 rounded-[3rem] border border-slate-100 shadow-sm">
      <div className="flex items-center justify-between mb-8">
        <h2 className="text-xl font-black text-slate-900 flex items-center gap-3">
          <div className="w-10 h-10 bg-blue-50 rounded-xl flex items-center justify-center text-blue-600">
            <SafeIcon icon={FiCalendar} />
          </div>
          Business Calendar
        </h2>
        <div className="flex items-center gap-4 bg-slate-50 p-2 rounded-2xl">
          <button onClick={prevMonth} className="p-2 hover:bg-white rounded-xl transition-all text-slate-400 hover:text-slate-900">
            <SafeIcon icon={FiChevronLeft} />
          </button>
          <span className="font-black text-sm text-slate-900 uppercase tracking-widest min-w-[120px] text-center">
            {format(currentMonth, 'MMMM yyyy')}
          </span>
          <button onClick={nextMonth} className="p-2 hover:bg-white rounded-xl transition-all text-slate-400 hover:text-slate-900">
            <SafeIcon icon={FiChevronRight} />
          </button>
        </div>
      </div>

      <div className="grid grid-cols-7 gap-2">
        {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map(d => (
          <div key={d} className="text-[10px] font-black text-slate-400 uppercase tracking-widest text-center py-2">
            {d}
          </div>
        ))}
        {days.map((day, idx) => {
          const events = getEventsForDay(day);
          return (
            <div 
              key={day.toString()} 
              className={`min-h-[100px] p-3 rounded-2xl border transition-all ${
                isSameDay(day, new Date()) ? 'bg-blue-50/50 border-blue-100 ring-2 ring-blue-50' : 'bg-slate-50/30 border-transparent hover:border-slate-100'
              }`}
            >
              <span className={`text-xs font-black ${isSameDay(day, new Date()) ? 'text-blue-600' : 'text-slate-400'}`}>
                {format(day, 'd')}
              </span>
              <div className="mt-2 space-y-1">
                {events.slice(0, 3).map((event, eIdx) => (
                  <div 
                    key={eIdx} 
                    className={`text-[9px] font-bold py-1 px-2 rounded-lg truncate ${
                      event.type === 'task' ? 'bg-white text-slate-600 border border-slate-100' : 'bg-blue-600 text-white'
                    }`}
                  >
                    {event.title || event.name}
                  </div>
                ))}
                {events.length > 3 && (
                  <div className="text-[8px] font-black text-slate-400 text-center uppercase tracking-tighter">
                    +{events.length - 3} more
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default DashboardCalendar;